import { Hash, Lock, Users } from "lucide-react";
import { useEffect, useState } from "react";
import { api } from "../../api";
import { Avatar } from "../../components/primitives";

export type SearchResult = {
  id: string;
  name: string;
  secondary?: string;
  avatarUrl?: string;
  isPrivate?: boolean;
  kind?: "user" | "channel" | "group";
};
export type SuggestionAnchor = { left: number; bottom: number; maxHeight: number };

export function Autocomplete({
  trigger,
  query,
  anchor,
  onPick,
  onDismiss,
  id,
}: {
  trigger: "@" | "#";
  query: string;
  anchor: SuggestionAnchor | null;
  onPick: (item: SearchResult) => void;
  onDismiss: () => void;
  id: string;
}) {
  const [items, setItems] = useState<SearchResult[]>([]);
  const [selected, setSelected] = useState(0);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    let active = true;
    setSelected(0);
    setLoading(true);
    const isSlackUserId = trigger === "@" && /^[UW][A-Z0-9]{5,}$/i.test(query);
    const timer = window.setTimeout(
      () => {
        const call =
          trigger === "@"
            ? Promise.all([api.users(query), api.usergroups()]).then(([users, groups]) => {
                const normalized = query.trim().toLowerCase();
                const matchingGroups = groups.filter(
                  (group) =>
                    !normalized ||
                    group.handle.toLowerCase().includes(normalized) ||
                    group.name.toLowerCase().includes(normalized),
                );
                return [
                  ...matchingGroups.slice(0, 3).map((group) => ({
                    id: group.id,
                    name: group.handle,
                    kind: "group" as const,
                    secondary: `${group.name} · ${group.memberCount} ${
                      group.memberCount === 1 ? "member" : "members"
                    } · user group`,
                  })),
                  ...users.map((user) => ({
                    id: user.id,
                    name: user.name,
                    kind: "user" as const,
                    secondary:
                      user.realName && user.realName !== user.name
                        ? `@${user.handle} · ${user.realName}`
                        : `@${user.handle}`,
                    avatarUrl: user.avatarUrl,
                  })),
                ];
              })
            : api.channels(query).then((channels) =>
                channels.map((channel) => ({
                  id: channel.id,
                  name: channel.name,
                  isPrivate: channel.isPrivate,
                  kind: "channel" as const,
                })),
              );
        call
          .then((results) => {
            if (active) {
              setItems(results);
              setLoading(false);
            }
          })
          .catch(() => {
            if (active) {
              setItems([]);
              setLoading(false);
            }
          });
      },
      isSlackUserId ? 0 : 180,
    );
    return () => {
      active = false;
      window.clearTimeout(timer);
    };
  }, [query, trigger]);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.metaKey || event.ctrlKey) return;
      if (event.key === "Escape") {
        event.preventDefault();
        onDismiss();
      } else if (event.key === "ArrowDown" && items.length) {
        event.preventDefault();
        setSelected((current) => (current + 1) % items.length);
      } else if (event.key === "ArrowUp" && items.length) {
        event.preventDefault();
        setSelected((current) => (current - 1 + items.length) % items.length);
      } else if (event.key === "Enter" || event.key === "Tab") {
        const item = items[selected];
        if (item) {
          event.preventDefault();
          onPick(item);
        }
      }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [items, onDismiss, onPick, selected]);

  return (
    <div
      id={id}
      className="autocomplete caret-autocomplete"
      role="listbox"
      aria-label={trigger === "@" ? "People and user groups" : "Channels"}
      aria-busy={loading}
      style={anchor ?? undefined}
    >
      <div className="autocomplete-title">
        <span>{trigger === "@" ? "People & user groups" : "Channels"}</span>
        <kbd>↑↓</kbd>
      </div>
      {loading && <div className="autocomplete-status">Searching…</div>}
      {!loading && !items.length && (
        <div className="autocomplete-status">No {trigger === "@" ? "people or groups" : "channels"} found</div>
      )}
      {items.slice(0, 8).map((item, index) => (
        <button
          id={`${id}-${index}`}
          ref={
            selected === index
              ? (node) => {
                  node?.scrollIntoView({ block: "nearest" });
                }
              : undefined
          }
          type="button"
          role="option"
          aria-selected={selected === index}
          className={selected === index ? "selected" : ""}
          key={`${item.kind ?? "channel"}_${item.id}`}
          onMouseEnter={() => setSelected(index)}
          onMouseDown={(event) => event.preventDefault()}
          onClick={() => onPick(item)}
        >
          {item.kind === "group" ? (
            <span className="channel-result-icon">
              <Users size={14} />
            </span>
          ) : item.kind === "user" ? (
            <Avatar name={item.name} src={item.avatarUrl} size={24} />
          ) : (
            <span className="channel-result-icon">{item.isPrivate ? <Lock size={12} /> : <Hash size={14} />}</span>
          )}
          <span>
            <strong>
              {item.kind === "user" ? "" : item.kind === "group" ? "@" : "#"}
              {item.name}
            </strong>
            <small>
              {item.secondary ??
                (item.isPrivate ? "Private channel" : item.kind === "channel" ? "Channel" : "Slack member")}
            </small>
          </span>
        </button>
      ))}
    </div>
  );
}
